<script>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf, url } from '@vuelidate/validators';
import FileInput from './FileInput.vue';

const MediaFormats = ['document', 'image', 'video'];

const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

const mediaTemplateValidator = (value, vm) => {
  // For media templates, don't validate body variables
  if (vm.hasMediaHeader) {
    return true;
  }
  // For text templates, require all variables to be filled
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    FileInput,
    NextButton,
  },
  props: {
    template: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['sendMessage', 'resetTemplate'],
  setup(props, { emit }) {
    const processedParams = ref({});
    const headerProcessedParams = ref({});
    const headerMediaUrl = ref('');
    const headerComponent = ref(null);
    const acceptedTypes = {
      image: 'image/*',
      video: 'video/*',
      document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
    };

    const processVariable = str => {
      return str.replace(/{{|}}/g, '');
    };

    const templateString = computed(() => {
      if (!props.template || !props.template.components) {
        return '';
      }
      const bodyComponent = props.template.components.find(
        component => component.type === 'BODY'
      );
      return bodyComponent ? bodyComponent.text : '';
    });

    const variables = computed(() => {
      if (!templateString.value) return null;
      return templateString.value.match(/{{([^}]+)}}/g);
    });

    const headerVariables = computed(() => {
      const variables = headerString.value?.match(/{{([^}]+)}}/g);
      return variables;
    });

    const hasHeader = computed(() => {
      return !!headerComponent.value;
    });

    const hasMediaHeader = computed(() => {
      return (
        hasHeader.value &&
        MediaFormats.includes(headerComponent.value.format?.toLowerCase())
      );
    });

    const headerString = computed(() => {
      return headerComponent.value?.format?.toUpperCase() === 'TEXT'
        ? headerComponent.value.text
        : null;
    });

    const headerMediaType = computed(() => {
      return headerComponent.value?.format?.toUpperCase();
    });

    const processedString = computed(() => {
      if (!templateString.value) return '';
      return templateString.value.replace(/{{([^}]+)}}/g, (match, variable) => {
        const variableKey = processVariable(variable);
        return processedParams.value[variableKey] || `{{${variable}}}`;
      });
    });

    const headerProcessedString = computed(() => {
      if (!headerString.value) return '';
      return headerString.value.replace(/{{([^}]+)}}/g, (match, variable) => {
        const variableKey = processVariable(variable);
        return headerProcessedParams.value[variableKey] || `{{${variable}}}`;
      });
    });

    const acceptedType = computed(() => {
      return acceptedTypes[headerMediaType.value?.toLowerCase()];
    });

    const requireBodyVariables = computed(() => {
      // Don't require body variables if we have a media header
      const result =
        variables.value && variables.value.length > 0 && !hasMediaHeader.value;
      return result;
    });

    const v$ = useVuelidate(
      {
        processedParams: {
          requiredIfKeysPresent: requiredIf(() => requireBodyVariables.value),
        },
        headerMediaUrl: {
          required: requiredIf(() => hasMediaHeader.value),
          url: url,
        },
      },
      { processedParams, headerMediaUrl }
    );

    const generateVariables = () => {
      if (!templateString.value) return;

      const matchedVariables = templateString.value.match(/{{([^}]+)}}/g);
      if (!matchedVariables) return;

      const finalVars = matchedVariables.map(i => processVariable(i));
      processedParams.value = finalVars.reduce((acc, variable) => {
        acc[variable] = '';
        return acc;
      }, {});
    };

    const extractHeaderComponent = () => {
      if (!props.template || !props.template.components) return;

      headerComponent.value = props.template.components.find(
        component => component.type === 'HEADER'
      );
    };

    const onFileInput = fileUrl => {
      headerMediaUrl.value = fileUrl;
    };

    const resetTemplate = () => {
      emit('resetTemplate');
    };

    const buildWhatsAppTemplateComponent = () => {
      const components = [];

      if (hasMediaHeader.value && headerMediaUrl.value) {
        const headerComponent = {
          type: 'header',
          parameters: [
            {
              type: headerMediaType.value.toLowerCase(),
              [headerMediaType.value.toLowerCase()]: {
                link: headerMediaUrl.value,
              },
            },
          ],
        };
        components.push(headerComponent);
      }

      if (Object.keys(headerProcessedParams.value).length > 0) {
        const headerComponent = {
          type: 'header',
          parameters: Object.keys(headerProcessedParams.value).map(key => ({
            type: 'text',
            text: headerProcessedParams.value[key],
          })),
        };
        components.push(headerComponent);
      }

      if (Object.keys(processedParams.value).length > 0) {
        const bodyComponent = {
          type: 'body',
          parameters: Object.keys(processedParams.value).map(key => ({
            type: 'text',
            parameter_name: key,
            text: processedParams.value[key],
          })),
        };
        components.push(bodyComponent);
      }
      return components;
    };

    const sendMessage = () => {
      if (!v$.value) {
        return;
      }

      // For media templates, skip validation if media is uploaded
      if (hasMediaHeader.value && headerMediaUrl.value) {
      } else if (hasMediaHeader.value && !headerMediaUrl.value) {
        return;
      } else {
        v$.value.$touch();
        if (v$.value.$invalid) {
          return;
        }
      }

      try {
        const payload = {
          message: processedString.value,
          templateParams: {
            name: props.template.name,
            category: props.template.category,
            language: props.template.language,
            namespace: props.template.namespace,
            processed_params: processedParams.value,
            components: buildWhatsAppTemplateComponent(),
          },
        };

        emit('sendMessage', payload);
      } catch (error) {
        // You could emit an error event here if needed
      }
    };

    onMounted(() => {
      generateVariables();
      extractHeaderComponent();
    });

    return {
      processedParams,
      headerProcessedParams,
      headerMediaUrl,
      headerComponent,
      acceptedTypes,
      variables,
      headerVariables,
      templateString,
      hasHeader,
      hasMediaHeader,
      headerString,
      headerMediaType,
      processedString,
      headerProcessedString,
      acceptedType,
      requireBodyVariables,
      v$,
      onFileInput,
      resetTemplate,
      sendMessage,
    };
  },
};
</script>

<template>
  <div v-if="template && template.components" class="w-full">
    <div v-if="hasHeader">
      <p class="section">Header</p>

      <div v-if="hasMediaHeader" class="p-2.5">
        <div class="items-center flex mb-2.5">
          <span
            class="bg-n-alpha-black2 text-n-slate-12 inline-block rounded-md text-xs py-2.5 px-6"
          >
            {{ headerMediaType }}
          </span>
          <FileInput :accept="acceptedType" @input="onFileInput" />
        </div>
        <p
          v-if="
            v$ &&
            v$.headerMediaUrl &&
            v$.headerMediaUrl.$dirty &&
            v$.headerMediaUrl.$invalid
          "
          class="error"
        >
          Invalid URL
        </p>
      </div>

      <div v-if="headerString">
        <textarea
          v-model="headerProcessedString"
          rows="4"
          readonly
          class="template-input"
        />
        <div v-if="headerVariables" class="p-2.5">
          <p class="text-sm font-semibold mb-2.5">
            {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
          </p>
          <div
            v-for="(variable, key) in headerProcessedParams"
            :key="key"
            class="items-center flex mb-2.5"
          >
            <span
              class="bg-n-alpha-black2 text-n-slate-12 inline-block rounded-md text-xs py-2.5 px-6"
            >
              {{ key }}
            </span>
            <woot-input
              v-model="headerProcessedParams[key]"
              type="text"
              class="flex-1 text-sm ml-2.5"
              :styles="{ marginBottom: 0 }"
            />
          </div>
          <p v-if="v$ && v$.$dirty && v$.$invalid" class="error">
            {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
          </p>
        </div>
      </div>
    </div>

    <div>
      <p class="section">Body</p>
      <textarea
        v-model="processedString"
        rows="4"
        readonly
        class="template-input"
      />
      <div v-if="hasMediaHeader && !variables" class="p-2.5">
        <p class="text-sm text-n-slate-12 bg-n-slate-3 p-2 rounded">
          This media template doesn't require body variables. You can send it
          directly after uploading the media file.
        </p>
      </div>
      <div v-if="variables" class="p-2.5">
        <p class="text-sm font-semibold mb-2.5">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams"
          :key="key"
          class="items-center flex mb-2.5"
        >
          <span
            class="bg-n-alpha-black2 text-n-slate-12 inline-block rounded-md text-xs py-2.5 px-6"
          >
            {{ key }}
          </span>
          <woot-input
            v-model="processedParams[key]"
            type="text"
            class="flex-1 text-sm ml-2.5"
            :styles="{ marginBottom: 0 }"
          />
        </div>
        <p v-if="v$ && v$.$dirty && v$.$invalid" class="error">
          {{
            hasMediaHeader
              ? 'Please upload a media file to send this template.'
              : $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE')
          }}
        </p>
      </div>
      <p
        v-if="v$ && v$.$dirty && v$.$invalid"
        class="bg-n-ruby-9/20 rounded-md text-n-ruby-9 p-2.5 text-center"
      >
        {{
          hasMediaHeader
            ? 'Please upload a media file to send this template.'
            : $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE')
        }}
      </p>
    </div>
    <footer class="flex justify-end gap-2">
      <NextButton
        faded
        slate
        type="reset"
        :label="$t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL')"
        @click="resetTemplate"
      />
      <NextButton
        type="button"
        :label="$t('WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL')"
        @click="sendMessage"
      />
    </footer>
  </div>
  <div v-else class="w-full p-4 text-center">
    <p class="text-n-slate-12">Loading template...</p>
  </div>
</template>
